import React, {useCallback, useMemo, useState} from 'react';

import {DeleteUserDialog} from 'src/components/delete-user-dialog';
import {NOTIFICATION_TIMEOUTS} from 'src/constants';
import {PB_ADMIN} from 'src/hooks/access-resource-constant';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {uninviteUserById} from 'src/openapi/admin/apis/UserIdentityApi';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {Tooltip} from '@pb/kit-component-popups';
import Typography from '@pb/kit-component-typography';
import {useShown} from '@pb/kit-hooks-use-shown';

import {useVendorUsers} from '../hooks/use-vendor-users';
import {section} from '../vendor-profile.module.styl';

import {AddVendorUserDialog} from './add-user/add-vendor-user-dialog';
import {AssignedUsersTable} from './assigned-users-table';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';
import type {Vendor} from 'src/openapi/admin/models/Vendor';


type Props = {
    vendor?: Vendor | null,
};

const MAX_USERS_PER_VENDOR = 20;

export const AssignedUsersSection: React.FC<Props> = ({vendor}) => {
    const {shown, onToggle, onClose} = useShown();
    const {executeWithNotifications} = useApiWithNotifications();
    const {
        users,
        loading,
        error,
        reload,
        removeUserFromState,
    } = useVendorUsers(vendor?.id);
    const [removingMap, setRemovingMap] = useState<Record<number, boolean>>({});
    const [userToDelete, setUserToDelete] = useState<number | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const handleOpen = useCallback(() => onToggle(), [onToggle]);
    const handleClose = useCallback(() => onClose(), [onClose]);

    const isBtnDisabled = !vendor || users.length >= MAX_USERS_PER_VENDOR;
    const {hasAccessToResource} = useCurrentUser();
    const canAddUser = hasAccessToResource(PB_ADMIN.UPDATE_USER);

    const handleRemoveClick = useCallback((userId: number) => {
        setUserToDelete(userId);
    }, []);

    const handleDeleteClose = useCallback(() => {
        if (!deleteBusy) {
            setUserToDelete(null);
        }
    }, [deleteBusy]);

    const handleConfirmDelete = useCallback(async () => {
        if (!vendor?.id || userToDelete == null) {
            return;
        }

        setDeleteBusy(true);
        setRemovingMap(prev => ({...prev, [userToDelete]: true}));

        try {
            const response = await executeWithNotifications<ApiError>(
                uninviteUserById({userId: userToDelete}),
                {
                    success: 'User successfully removed from vendor',
                    error: 'Unable to remove user. Please try again',
                    exception: 'Unable to remove user. Please try again',
                },
                `vendor-unassign-user-${userToDelete}`,
                NOTIFICATION_TIMEOUTS,
            );

            if (response?.success) {
                removeUserFromState(userToDelete);
            }
        } finally {
            setRemovingMap(prev => {
                const next = {...prev};
                if (userToDelete != null) {
                    delete next[userToDelete];
                }
                return next;
            });
            setDeleteBusy(false);
            setUserToDelete(null);
        }
    }, [executeWithNotifications, removeUserFromState, userToDelete, vendor?.id]);

    const userCountLabel = useMemo(() => (
        users.length > 0 ? ` (${users.length})` : ''
    ), [users.length]);

    const renderAddButton = () => {
        if (!canAddUser) {
            return null;
        }

        if (isBtnDisabled) {
            return (
                <Tooltip
                    placement="right"
                    color="light"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <FlatButton
                            startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                            disabled={true}
                            ref={anchorRef}
                            {...getAnchorProps()}>
                            <b>Add User</b>
                        </FlatButton>
                    )}>
                    The vendor profile already contains the maximum allowed number of users
                </Tooltip>
            );
        }

        return (
            <FlatButton
                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                onClick={handleOpen}>
                <b>Add User</b>
            </FlatButton>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Group direction="h" alignItems="center" gap="xs">
                    <Loading type="ICON"/>
                    <Typography>Loading users...</Typography>
                </Group>
            );
        }

        if (users.length === 0) {
            return (
                <Typography fontWeight="semi-bold">
                    There are currently no users associated with this Vendor.
                    Please use the "Add User" button to add a new user to this vendor.
                </Typography>
            );
        }

        return (
            <AssignedUsersTable
                users={users}
                deletingMap={removingMap}
                onDelete={handleRemoveClick}
            />
        );
    };

    if (!vendor) {
        return (
            <div className={section}>
                <Group direction="v" gap="m" style={{padding: 16}}>
                    <Group direction="h" alignItems="center" gap="s">
                        <Typography fontWeight="semi-bold" variant="heading-level-6">
                            Users
                        </Typography>
                    </Group>
                    <Group direction="h" alignItems="center" gap="xs">
                        <Loading type="ICON"/>
                        <Typography>Loading users...</Typography>
                    </Group>
                </Group>
            </div>
        );
    }

    return (
        <div className={section}>
            <Group direction="v" gap="m" style={{padding: 16}}>
                <Group direction="h" alignItems="center" gap="s">
                    <Group direction="h" alignItems="center" gap="s">
                        <Typography fontWeight="semi-bold" variant="heading-level-6">
                            {`Users${userCountLabel}`}
                        </Typography>
                    </Group>
                    {renderAddButton()}
                </Group>

                {error ? (
                    <Message color="error">
                        Unable to load assigned users. Please try again.
                    </Message>
                ) : null}

                {renderContent()}
            </Group>
            <AddVendorUserDialog
                showDialog={shown}
                closeDialog={handleClose}
                vendorId={vendor?.id}
                onUserAdded={reload}
            />
            <DeleteUserDialog
                shown={userToDelete !== null}
                onClose={handleDeleteClose}
                onConfirm={handleConfirmDelete}
                busy={deleteBusy}
            />
        </div>
    );
};
